// =============================================================================
// Shell da aplicação
//
// Navegação validada com o Financeiro em 23/09, por protótipo navegável:
//
//   ORÇAMENTO
//     Visão financeira ....... abas Receita · Despesa · Orçado vs Realizado
//     Painel do Gestor ....... abas Gerencial · Analítica · Orçamento do ano seguinte (071)
//     OPERACIONAL (subseção)
//       Solicitações ......... pedidos de aumento e remanejamento  [contador]
//       Planilha orçamentária  abas Planilha · Orçamento × área (071) · Categorias por área (081) · Lançar · Importar
//       Plano de contas e centros de custo (era "Cadastros")
//       Pendências ........... a Central Operacional              [contador]
//   COMISSÕES
//     Apuração · Produtos
//   CONFIGURAÇÕES
//     Acessos · Integrações · Histórico
//
// Lançar (era "Cadastrar") e Importar viraram abas da Planilha em 20/10, a
// pedido do Financeiro: o menu vai receber outras frentes (assinaturas, contas a
// pagar e a receber), e um "Importar" solto não dizia importar o quê.
//
// O gestor de centro de custo vê só o Painel do Gestor. Seções e subseções
// abrem e fecham; fechada, a seção mostra a soma dos contadores de dentro e um
// ponto laranja se a tela aberta estiver nela — para recolher não esconder o
// que exige ação. No computador, a barra inteira também recolhe numa faixa só
// de ícones, pelo mesmo princípio: o contador continua lá, em miniatura.
//
// A competência mora aqui, não dentro de cada tela — quem alterna entre a visão
// consolidada e a do gestor espera continuar no mesmo mês.
//
// Navegação por estado, sem router: ver `docs/PROXIMOS_PASSOS.md`.
// =============================================================================

import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, buscarContadores, type Aviso, type Contadores } from "./lib/api.ts";
import { PainelGestor } from "./paginas/PainelGestor.tsx";
import { VisaoFinanceiro } from "./paginas/VisaoFinanceiro.tsx";
import { CadastroOrcamento } from "./paginas/CadastroOrcamento.tsx";
import { CentralOperacional } from "./paginas/CentralOperacional.tsx";
import { ImportarOrcamento } from "./paginas/ImportarOrcamento.tsx";
import { PropostaGestor } from "./paginas/PropostaGestor.tsx";
import { FerramentasArea } from "./paginas/FerramentasArea.tsx";
import { AnaliseOrcamento } from "./paginas/AnaliseOrcamento.tsx";
import { Acessos } from "./paginas/Acessos.tsx";
import { Integracoes } from "./paginas/Integracoes.tsx";
import { PlanilhaOrcamento } from "./paginas/PlanilhaOrcamento.tsx";
import { Cadastros } from "./paginas/Cadastros.tsx";
import { Historico } from "./paginas/Historico.tsx";
import { Receita } from "./paginas/Receita.tsx";
import { OrcadoRealizado } from "./paginas/OrcadoRealizado.tsx";
import { CategoriasArea } from "./paginas/CategoriasArea.tsx";
import { Solicitacoes } from "./paginas/Solicitacoes.tsx";
import { ComissaoApuracao } from "./paginas/ComissaoApuracao.tsx";
import { ComissaoProdutos } from "./paginas/ComissaoProdutos.tsx";
import { Ferramentas } from "./paginas/Ferramentas.tsx";
import { ImportarFerramentas } from "./paginas/ImportarFerramentas.tsx";
import { Icone, type NomeIcone } from "./componentes/Icone.tsx";
import { Sino } from "./componentes/Sino.tsx";

type Pagina =
  | "receita"
  | "despesa"
  | "ovr"
  | "gestor"
  | "analitica"
  | "gestorProposta"
  | "gestorFerramentas"
  | "orcamentoArea"
  | "categoriasArea"
  | "solicitacoes"
  | "planilha"
  | "cadastro"
  | "importar"
  | "cadastros"
  | "pendencias"
  | "acessos"
  | "integracoes"
  | "historico"
  | "comissao"
  | "comissaoProdutos"
  | "ferramentas"
  | "ferramentasImportar";

type Eu = {
  autenticado: boolean;
  usuario: { id: string; nome: string; email: string };
  papeis: string[];
  /** Perfil Dev (migration 057): tudo o que a pessoa vê vem do staging. */
  perfil_dev?: boolean;
};

const FIN = ["financeiro", "admin"];

/**
 * `papeis`: quem vê o item. Sem o campo, todos veem.
 *
 * ⚠️ Esconder o item é CONFORTO, não segurança: quem decide o que cada um pode
 * fazer é a RLS e a RPC. Um gestor que chamasse a rota de Acessos na mão seria
 * recusado do mesmo jeito — só não precisa ver um botão que não funciona.
 */
type Item = {
  id: Pagina;
  rotulo: string;
  meta?: string;
  descricao: string;
  icone: NomeIcone;
  papeis?: string[];
  /** Páginas que acendem este item: as abas dele. */
  abrange?: Pagina[];
  /** Qual contador aparece no item. */
  contador?: keyof Contadores;
};
type Secao = { rotulo: string; itens: Item[]; sub?: { rotulo: string; itens: Item[] } };

const NAV: Secao[] = [
  {
    rotulo: "Orçamento",
    itens: [
      {
        id: "despesa",
        rotulo: "Visão financeira",
        descricao: "Receita, despesa e orçado vs realizado — todos os centros de custo",
        icone: "painel",
        papeis: FIN,
        abrange: ["receita", "ovr"],
      },
      {
        // "Painel do Gestor" é o nome do protótipo e do AF-01 §10; "Meu
        // orçamento" continua sendo o título DENTRO da tela.
        id: "gestor",
        rotulo: "Painel do Gestor",
        descricao: "Centro de custo sob sua responsabilidade",
        icone: "medidor",
        abrange: ["analitica", "gestorProposta", "gestorFerramentas"],
      },
    ],
    sub: {
      rotulo: "Operacional",
      itens: [
        {
          id: "solicitacoes",
          rotulo: "Solicitações",
          meta: "Aumento, remanejamento e ferramentas",
          descricao: "Pedidos dos gestores, que o Financeiro aprova ou recusa",
          icone: "caixaEntrada",
          papeis: FIN,
          contador: "solicitacoes",
        },
        {
          // Lançar e Importar são abas daqui (ver o cabeçalho). A descrição vale
          // para as três, porque é ela que o topo mostra em qualquer uma.
          id: "planilha",
          rotulo: "Planilha orçamentária",
          descricao: "O orçamento do ano: editar como no Excel, lançar linha nova ou importar",
          icone: "planilha",
          papeis: FIN,
          abrange: ["orcamentoArea", "categoriasArea", "cadastro", "importar"],
        },
        // Plano de contas e Solicitações ficam no menu, e não viram abas: os dois
        // vão servir também às próximas frentes (Assinaturas, por exemplo), e
        // dentro da Planilha pareceriam ser só do orçamento. Decidido em 20/10.
        { id: "cadastros", rotulo: "Plano de contas e centros de custo", descricao: "Vindos do Conta Azul", icone: "arvore", papeis: FIN },
        {
          // Continua a um clique: a subseção é rótulo, e abre expandida.
          id: "pendencias",
          rotulo: "Pendências",
          descricao: "O que exige decisão do Financeiro",
          icone: "alerta",
          papeis: FIN,
          contador: "pendencias",
        },
      ],
    },
  },
  {
    // Frente 11 (Contratos e Assinaturas), começada em 21/10/2026 a partir da
    // planilha "Controle Ferramentas" do Financeiro. As abas Cobranças e Painel
    // entram com o reconhecimento das cobranças do Conta Azul.
    rotulo: "Ferramentas",
    itens: [
      {
        id: "ferramentas",
        rotulo: "Ferramentas e assinaturas",
        descricao: "Cadastro das assinaturas: valor, cobrança, área e responsável",
        icone: "ferramenta",
        papeis: FIN,
        abrange: ["ferramentasImportar"],
      },
    ],
  },
  {
    // Frente 8, trazida do sistema que rodava no Fly (comissao-cf.fly.dev).
    rotulo: "Comissões",
    itens: [
      { id: "comissao", rotulo: "Apuração", meta: "Professores parceiros", descricao: "O repasse do mês: Pix e TMB do mês anterior, cartão e boleto adiantados", icone: "porcentagem", papeis: FIN },
      { id: "comissaoProdutos", rotulo: "Produtos", descricao: "Variações na Guru, grupo na TMB, parceiro, percentual e regra", icone: "pacote", papeis: FIN },
    ],
  },
  {
    rotulo: "Configurações",
    itens: [
      { id: "acessos", rotulo: "Acessos", descricao: "Gestores e os centros de custo de cada um", icone: "pessoas", papeis: FIN },
      { id: "integracoes", rotulo: "Integrações", descricao: "Conta Azul e eNotas", icone: "tomada", papeis: FIN },
      { id: "historico", rotulo: "Histórico", descricao: "Quem fez o quê, com antes e depois", icone: "historico", papeis: FIN },
    ],
  },
];

/** As abas de cada item. `meta` é a linha de apoio — nas visões, o nome
 *  equivalente no PRD (AF-01 §10). */
const ABAS: Partial<Record<Pagina, { id: Pagina; rotulo: string; meta: string }[]>> = {
  despesa: [
    { id: "receita", rotulo: "Receita", meta: "Visão Executiva" },
    { id: "despesa", rotulo: "Despesa", meta: "Visão Financeiro" },
    { id: "ovr", rotulo: "Orçado vs Realizado", meta: "Planilha mês a mês" },
  ],
  gestor: [
    { id: "gestor", rotulo: "Gerencial", meta: "Gráficos" },
    { id: "analitica", rotulo: "Analítica", meta: "Orçado vs Realizado" },
    // As ferramentas da área e o pedido de ferramenta (072). Antes do orçamento
    // do ano seguinte, a pedido do Enzo.
    { id: "gestorFerramentas", rotulo: "Ferramentas", meta: "Da área e pedidos" },
    // O orçamento do ano seguinte, que a área monta e envia (071).
    { id: "gestorProposta", rotulo: `Orçamento ${new Date().getFullYear() + 1}`, meta: "Proposta da área" },
  ],
  planilha: [
    { id: "planilha", rotulo: "Planilha", meta: "O ano inteiro" },
    // O orçado em análise: o que as áreas pediram, antes de entrar na Planilha (071).
    { id: "orcamentoArea", rotulo: "Orçamento × área", meta: "Em análise" },
    // Quem orça cada categoria na proposta das áreas (081).
    { id: "categoriasArea", rotulo: "Categorias por área", meta: "Quem orça" },
    { id: "cadastro", rotulo: "Lançar", meta: "Linha nova" },
    { id: "importar", rotulo: "Importar", meta: "Por planilha" },
  ],
  ferramentas: [
    { id: "ferramentas", rotulo: "Ferramentas", meta: "Cadastro" },
    { id: "ferramentasImportar", rotulo: "Importar", meta: "Planilha do Financeiro" },
  ],
};

function competenciaCorrente(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-01`;
}

/** O período abre no mês corrente — uma competência só, que é o caso de sempre
 *  e o único caminho que a API atende sem agregar. */
export const periodoInicial = (): string[] => [competenciaCorrente()];

function podeVer(papeis: string[], exigidos?: string[]): boolean {
  return !exigidos || exigidos.some((p) => papeis.includes(p));
}

// Seções recolhidas: conforto de cada pessoa, no navegador dela. Se o
// armazenamento falhar (janela privada, dado bloqueado), tudo abre expandido.
const CHAVE_RECOLHIDAS = "nexa.sidebar.recolhidas";
function lerRecolhidas(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CHAVE_RECOLHIDAS) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}
function gravarRecolhidas(s: Set<string>) {
  try {
    localStorage.setItem(CHAVE_RECOLHIDAS, JSON.stringify([...s]));
  } catch {
    /* sem armazenamento: a escolha vale só nesta visita */
  }
}

// A barra inteira recolhida, no mesmo espírito: conforto de cada pessoa, e se
// o armazenamento falhar ela abre expandida.
const CHAVE_BARRA_RECOLHIDA = "nexa.sidebar.recolhida";
function lerBarraRecolhida(): boolean {
  try {
    return localStorage.getItem(CHAVE_BARRA_RECOLHIDA) === "1";
  } catch {
    return false;
  }
}
function gravarBarraRecolhida(recolhida: boolean) {
  try {
    localStorage.setItem(CHAVE_BARRA_RECOLHIDA, recolhida ? "1" : "0");
  } catch {
    /* sem armazenamento: a escolha vale só nesta visita */
  }
}

/**
 * Se a tela tem a largura do `lg` do Tailwind (64rem), onde a sidebar é coluna
 * fixa e não menu sobreposto.
 *
 * A faixa de ícones só existe aí. No celular o menu abre por cima do conteúdo e
 * sempre inteiro — a escolha de recolher, guardada no computador, não vale lá.
 * Decidido por JS e não só por classe `lg:` porque a faixa muda a ESTRUTURA
 * (mostra os itens das seções fechadas, troca cabeçalho por separador), não só
 * o estilo.
 */
const TELA_LARGA = "(min-width: 64rem)";
function useTelaLarga(): boolean {
  const [larga, setLarga] = useState(() => {
    try {
      return window.matchMedia(TELA_LARGA).matches;
    } catch {
      return true;
    }
  });
  useEffect(() => {
    let consulta: MediaQueryList;
    try {
      consulta = window.matchMedia(TELA_LARGA);
    } catch {
      return;
    }
    const ouvir = () => setLarga(consulta.matches);
    ouvir();
    consulta.addEventListener("change", ouvir);
    return () => consulta.removeEventListener("change", ouvir);
  }, []);
  return larga;
}

/**
 * Aviso de login que não deu certo.
 *
 * `sem_acesso` é o caso que mais vai acontecer na entrada do sistema: a conta
 * Google é válida, mas ninguém cadastrou a pessoa. O login NÃO cria usuário, de
 * propósito (migration 015) — conceder acesso é ato deliberado.
 */
function AvisoLogin() {
  const motivo = new URLSearchParams(location.search).get("login");
  if (!motivo) return null;

  const texto: Record<string, string> = {
    sem_acesso:
      "Sua conta Google foi reconhecida, mas ainda não tem acesso ao Nexa Finance. " +
      "Peça ao Financeiro para cadastrar o seu e-mail.",
    cancelado: "Login cancelado. Tente de novo quando quiser.",
  };

  return (
    <div role="status" className="border-b border-laranja/30 bg-laranja/10">
      <div className="pagina flex items-center gap-3 py-2.5">
        <span aria-hidden className="text-[13px]">⚠️</span>
        <p className="text-[13px] text-tinta">
          {texto[motivo] ?? "Não foi possível concluir o login."}
        </p>
        <a
          href="/auth/login"
          className="ml-auto shrink-0 rounded-lg bg-laranja px-3 py-1 text-[12px] font-semibold text-white"
        >
          Entrar de novo
        </a>
      </div>
    </div>
  );
}

export function App() {
  const [pagina, setPagina] = useState<Pagina>("despesa");
  const [periodo, setPeriodo] = useState<string[]>(periodoInicial);
  const [menuAberto, setMenuAberto] = useState(false);
  const [solicitacaoAlvo, setSolicitacaoAlvo] = useState<string | null>(null);

  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<Eu>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  const papeis = eu.data?.papeis ?? [];
  const doFinanceiro = podeVer(papeis, FIN);

  // Os contadores da sidebar. Só para quem trata as filas.
  const contadores = useQuery({
    queryKey: ["contadores"],
    queryFn: buscarContadores,
    enabled: doFinanceiro,
    refetchInterval: 60_000,
  });

  const filtrar = (itens: Item[]) => itens.filter((i) => podeVer(papeis, i.papeis));
  const secoes = NAV.map((s) => ({
    ...s,
    itens: filtrar(s.itens),
    sub: s.sub ? { ...s.sub, itens: filtrar(s.sub.itens) } : undefined,
  })).filter((s) => s.itens.length > 0 || (s.sub?.itens.length ?? 0) > 0);
  const todosItens = secoes.flatMap((s) => [...s.itens, ...(s.sub?.itens ?? [])]);

  // Toda página que a pessoa alcança: os itens mais as abas deles.
  const alcancaveis = new Set<Pagina>(todosItens.flatMap((i) => [i.id, ...(i.abrange ?? [])]));

  // Página escondida para o papel não pode continuar aberta: o gestor, que não
  // vê a Visão financeira, cai no Painel do Gestor.
  useEffect(() => {
    if (!eu.data) return;
    if (!alcancaveis.has(pagina)) setPagina(todosItens[0]?.id ?? "gestor");
  }, [eu.data, pagina, alcancaveis, todosItens]);

  function ir(destino: Pagina) {
    setPagina(destino);
    setMenuAberto(false);
  }

  const itemAtual = todosItens.find((i) => i.id === pagina || i.abrange?.includes(pagina));
  const secaoAtual = secoes.find((s) => s.itens.includes(itemAtual!) || s.sub?.itens.includes(itemAtual!));
  const abas = itemAtual ? ABAS[itemAtual.id] : undefined;

  return (
    <div className="flex min-h-screen">
      <Sidebar
        secoes={secoes}
        pagina={pagina}
        aberto={menuAberto}
        usuario={eu.data?.usuario}
        papeis={eu.data?.perfil_dev ? ["dev"] : papeis}
        contadores={contadores.data}
        onIr={ir}
        onFechar={() => setMenuAberto(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-3 border-b border-linha bg-superficie px-4 py-3 lg:px-6">
          <button
            type="button"
            aria-label="Mostrar ou ocultar o menu"
            aria-expanded={menuAberto}
            onClick={() => setMenuAberto((v) => !v)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-linha text-[15px] text-tinta-media hover:bg-superficie-2 lg:hidden"
          >
            ☰
          </button>
          <div className="min-w-0">
            <p className="truncate font-display text-[14.5px] font-bold">
              {itemAtual?.rotulo ?? "Nexa Finance"}
            </p>
            <p className="truncate text-[11.5px] text-tinta-fraca">
              {secaoAtual ? `${secaoAtual.rotulo}${secaoAtual.sub?.itens.includes(itemAtual!) ? ` · ${secaoAtual.sub.rotulo}` : ""} · ` : ""}
              {itemAtual?.descricao ?? ""}
            </p>
          </div>
          {doFinanceiro && (
            <Sino
              onAbrir={(a: Aviso) => {
                if (a.solicitacao_id) setSolicitacaoAlvo(a.solicitacao_id);
                if (a.tipo === "ferramenta") {
                  // Abre Ferramentas já na aba Pendências: a página lê a aba guardada ao montar.
                  try { localStorage.setItem("nexa.ferramentas.aba", "pendencias"); } catch { /* sem armazenamento: abre na aba de costume */ }
                  ir("ferramentas");
                  return;
                }
                ir(a.tipo === "solicitacao" ? "solicitacoes" : "pendencias");
              }}
            />
          )}
        </div>

        <AvisoLogin />

        {eu.data?.perfil_dev && (
          <p
            role="status"
            className="border-b border-atencao-linha bg-atencao-bg px-4 py-2 text-[12.5px] text-atencao-tinta lg:px-6"
          >
            <strong>Perfil Dev · staging.</strong> Tudo nesta tela vem do banco de staging, com dados
            sintéticos. Nenhum número é real, e nada do que você fizer aqui chega à produção.
          </p>
        )}

        {abas && (
          <div className="pagina pt-4">
            <FaixaAbas abas={abas.filter((a) => alcancaveis.has(a.id))} pagina={pagina} onIr={ir} />
          </div>
        )}

        <main className="flex-1">
          {pagina === "receita" && <Receita periodo={periodo} onMudarPeriodo={setPeriodo} />}
          {pagina === "despesa" && <VisaoFinanceiro periodo={periodo} onMudarPeriodo={setPeriodo} />}
          {pagina === "ovr" && <OrcadoRealizado periodo={periodo} onMudarPeriodo={setPeriodo} escopo="financeiro" />}
          {pagina === "gestor" && <PainelGestor periodo={periodo} onMudarPeriodo={setPeriodo} />}
          {pagina === "analitica" && <OrcadoRealizado periodo={periodo} onMudarPeriodo={setPeriodo} escopo="gestor" />}
          {pagina === "gestorProposta" && <PropostaGestor />}
          {pagina === "gestorFerramentas" && <FerramentasArea />}
          {pagina === "orcamentoArea" && <AnaliseOrcamento />}
          {pagina === "categoriasArea" && <CategoriasArea />}
          {pagina === "solicitacoes" && <Solicitacoes inicial={solicitacaoAlvo} />}
          {pagina === "planilha" && <PlanilhaOrcamento periodo={periodo} onMudarPeriodo={setPeriodo} />}
          {pagina === "cadastro" && <CadastroOrcamento competencia={periodo[periodo.length - 1]!} />}
          {pagina === "importar" && <ImportarOrcamento />}
          {pagina === "cadastros" && <Cadastros />}
          {pagina === "pendencias" && <CentralOperacional />}
          {pagina === "acessos" && <Acessos />}
          {pagina === "integracoes" && <Integracoes />}
          {pagina === "historico" && <Historico />}
          {pagina === "comissao" && <ComissaoApuracao papeis={papeis} />}
          {pagina === "comissaoProdutos" && <ComissaoProdutos />}
          {pagina === "ferramentas" && <Ferramentas />}
          {pagina === "ferramentasImportar" && <ImportarFerramentas />}
        </main>
      </div>
    </div>
  );
}

// ── sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({
  secoes,
  pagina,
  aberto,
  usuario,
  papeis,
  contadores,
  onIr,
  onFechar,
}: {
  secoes: Secao[];
  pagina: Pagina;
  aberto: boolean;
  usuario?: { nome: string; email: string };
  papeis: string[];
  contadores?: Contadores;
  onIr: (p: Pagina) => void;
  onFechar: () => void;
}) {
  const [recolhidas, setRecolhidas] = useState<Set<string>>(lerRecolhidas);
  const alternar = (chave: string) =>
    setRecolhidas((atual) => {
      const n = new Set(atual);
      if (n.has(chave)) n.delete(chave); else n.add(chave);
      gravarRecolhidas(n);
      return n;
    });

  // A faixa de ícones é só do computador (ver `useTelaLarga`).
  const [barraRecolhida, setBarraRecolhida] = useState<boolean>(lerBarraRecolhida);
  const telaLarga = useTelaLarga();
  const estreita = barraRecolhida && telaLarga;
  const alternarBarra = () =>
    setBarraRecolhida((atual) => {
      gravarBarraRecolhida(!atual);
      return !atual;
    });

  const ativo = (i: Item) => i.id === pagina || !!i.abrange?.includes(pagina);
  const contagem = (i: Item) => (i.contador && contadores ? contadores[i.contador] : 0);
  const largura = estreita ? "w-[68px]" : "w-[272px]";

  return (
    <>
      {/* Véu do menu no celular. Só existe quando a sidebar está por cima. */}
      {aberto && (
        <button
          type="button"
          aria-label="Fechar o menu"
          onClick={onFechar}
          className="fixed inset-0 z-20 bg-azul/40 lg:hidden"
        />
      )}

      {/* A largura anima no `aside`; o conteúdo de dentro já nasce na largura
          final. Assim, ao abrir, os rótulos não se espremem e reflowam durante a
          transição: a coluna inteira é revelada, já composta. */}
      <aside
        id="menu-lateral"
        className={`fixed inset-y-0 left-0 z-30 flex shrink-0 overflow-hidden bg-azul-escuro text-azul-clara transition-[transform,translate,width] lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${largura} ${
          aberto ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className={`flex h-full shrink-0 flex-col ${largura}`}>
          {/* A marca vem da imagem, não de texto: o logotipo tem o "nexa" em
              branco, o "FINANCE" em laranja e o selo CF à direita. Na faixa
              estreita ele não cabe, e fica só o "CF". */}
          {estreita ? (
            <div className="flex shrink-0 justify-center border-b border-white/10 px-2 py-4">
              <span
                role="img"
                aria-label="Nexa Finance — Contabilidade Facilitada"
                title="Nexa Finance — Sistema Financeiro CF"
                className="flex size-9 items-center justify-center rounded-lg bg-white/[0.06] font-display text-[13px] font-extrabold tracking-tight text-white"
              >
                C<span className="text-laranja">F</span>
              </span>
            </div>
          ) : (
            <div className="shrink-0 border-b border-white/10 px-5 py-4">
              <img
                src="/logo-nexa-finance.png"
                alt="Nexa Finance — Contabilidade Facilitada"
                className="h-auto w-[196px] max-w-full"
                width={600}
                height={53}
              />
              <p className="mt-2 text-[11px] text-azul-fraca">Sistema Financeiro CF</p>
            </div>
          )}

          <nav
            aria-label="Módulos"
            className={`thin-scroll flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 ${estreita ? "space-y-1" : "space-y-5"}`}
          >
            {secoes.map((s, n) => {
              const todos = [...s.itens, ...(s.sub?.itens ?? [])];
              const chaveSub = s.sub ? `${s.rotulo}/${s.sub.rotulo}` : "";

              // Na faixa estreita não há o que abrir e fechar: o cabeçalho vira
              // separador e TODOS os itens aparecem, inclusive os de seção que a
              // pessoa fechou na barra larga. Um ícone escondido atrás de um
              // separador que não abre seria uma tela inalcançável.
              if (estreita) {
                return (
                  <div key={s.rotulo} role="group" aria-label={s.rotulo} className="space-y-1">
                    {n > 0 && <Separador />}
                    {s.itens.map((i) => (
                      <ItemNav key={i.id} item={i} ativo={ativo(i)} contagem={contagem(i)} estreita onIr={onIr} />
                    ))}
                    {s.sub && s.sub.itens.length > 0 && (
                      <div role="group" aria-label={s.sub.rotulo} className="space-y-1">
                        {s.itens.length > 0 && <Separador sub />}
                        {s.sub.itens.map((i) => (
                          <ItemNav key={i.id} item={i} ativo={ativo(i)} contagem={contagem(i)} estreita onIr={onIr} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={s.rotulo}>
                  <CabecalhoSecao
                    rotulo={s.rotulo}
                    fechada={recolhidas.has(s.rotulo)}
                    soma={todos.reduce((a, i) => a + contagem(i), 0)}
                    contemAtiva={todos.some(ativo)}
                    onAlternar={() => alternar(s.rotulo)}
                  />
                  {!recolhidas.has(s.rotulo) && (
                    <div className="mt-1 space-y-1">
                      {s.itens.map((i) => (
                        <ItemNav key={i.id} item={i} ativo={ativo(i)} contagem={contagem(i)} onIr={onIr} />
                      ))}
                      {s.sub && s.sub.itens.length > 0 && (
                        <div className="pt-3">
                          <CabecalhoSecao
                            sub
                            rotulo={s.sub.rotulo}
                            fechada={recolhidas.has(chaveSub)}
                            soma={s.sub.itens.reduce((a, i) => a + contagem(i), 0)}
                            contemAtiva={s.sub.itens.some(ativo)}
                            onAlternar={() => alternar(chaveSub)}
                          />
                          {!recolhidas.has(chaveSub) && (
                            <div className="mt-1 space-y-1">
                              {s.sub.itens.map((i) => (
                                <ItemNav key={i.id} item={i} ativo={ativo(i)} contagem={contagem(i)} onIr={onIr} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Recolher e abrir ficam no mesmo lugar nos dois estados, para quem
              recolheu achar o caminho de volta onde deixou. Só no computador: no
              celular o menu já se fecha pelo véu e pelo ☰. */}
          <div className="hidden shrink-0 px-3 pb-3 lg:block">
            <button
              type="button"
              onClick={alternarBarra}
              aria-controls="menu-lateral"
              aria-expanded={!estreita}
              aria-label={estreita ? "Abrir o menu" : "Recolher o menu"}
              title={estreita ? "Abrir o menu" : "Recolher o menu"}
              className={`flex w-full items-center rounded-lg py-2 text-[12px] font-semibold text-azul-fraca hover:bg-white/5 hover:text-white ${
                estreita ? "justify-center" : "gap-3 px-3.5"
              }`}
            >
              <Icone nome={estreita ? "abrirMenu" : "recolherMenu"} tamanho={18} />
              {!estreita && <span>Recolher o menu</span>}
            </button>
          </div>

          <div className={`shrink-0 border-t border-white/10 py-3 ${estreita ? "px-2 text-center" : "px-4"}`}>
            {usuario && !estreita && (
              <>
                <p className="truncate text-[12px] font-semibold text-white">{usuario.nome}</p>
                <p className="truncate text-[11px] text-azul-fraca">{usuario.email}</p>
                {papeis.length > 0 && (
                  <p className="mt-1 text-[10.5px] text-azul-rotulo">
                    {papeis.map((p) => ROTULO_PAPEL[p] ?? p).join(" · ")}
                  </p>
                )}
              </>
            )}
            {/* Na faixa estreita o nome some, mas sair continua a um clique —
                e a dica diz de quem é a sessão. */}
            <button
              type="button"
              onClick={sair}
              title={estreita && usuario ? `Sair — ${usuario.nome} (${usuario.email})` : undefined}
              className={`text-[11.5px] font-semibold text-azul-fraca hover:text-white ${estreita ? "" : "mt-2.5"}`}
            >
              Sair
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

/** O cabeçalho de seção na faixa estreita: só a linha, sem texto. */
function Separador({ sub = false }: { sub?: boolean }) {
  return (
    <div aria-hidden className={sub ? "px-3 py-1.5" : "px-1 py-2"}>
      <div className={`h-px ${sub ? "bg-white/[0.07]" : "bg-white/15"}`} />
    </div>
  );
}

/** Rótulo de seção que abre e fecha. Fechado, mostra o que ficaria escondido:
 *  a soma dos contadores e se a tela aberta está lá dentro. */
function CabecalhoSecao({
  rotulo,
  fechada,
  soma,
  contemAtiva,
  onAlternar,
  sub = false,
}: {
  rotulo: string;
  fechada: boolean;
  soma: number;
  contemAtiva: boolean;
  onAlternar: () => void;
  sub?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onAlternar}
      aria-expanded={!fechada}
      className={`flex w-full items-center gap-2 rounded-lg py-1 pr-2.5 pl-3.5 text-left font-display font-bold tracking-[0.1em] uppercase hover:bg-white/[0.04] ${
        sub ? "text-[10px] text-laranja/75" : "text-[11px] text-laranja"
      }`}
    >
      <span className="flex flex-1 items-center gap-2">
        {rotulo}
        {sub && <span aria-hidden className="h-px flex-1 bg-white/10" />}
      </span>
      {fechada && contemAtiva && (
        <span title="A tela aberta está nesta seção" className="size-[7px] shrink-0 rounded-full bg-laranja" />
      )}
      {fechada && soma > 0 && <Contador n={soma} />}
      <span aria-hidden className={`transition-transform ${fechada ? "-rotate-90" : ""}`}>
        <Icone nome="seta" tamanho={14} espessura={2.2} />
      </span>
    </button>
  );
}

/**
 * Um item do menu. Na faixa estreita (`estreita`), só o ícone: o rótulo e a
 * descrição vão para a dica, e o nome acessível vem do `aria-label`, porque o
 * texto visível deixa de existir.
 */
function ItemNav({
  item,
  ativo,
  contagem,
  onIr,
  estreita = false,
}: {
  item: Item;
  ativo: boolean;
  contagem: number;
  onIr: (p: Pagina) => void;
  estreita?: boolean;
}) {
  const emAberto = contagem > 0 ? ` (${contagem.toLocaleString("pt-BR")} em aberto)` : "";
  return (
    <button
      type="button"
      aria-current={ativo ? "page" : undefined}
      aria-label={estreita ? `${item.rotulo}${emAberto}` : undefined}
      title={estreita ? `${item.rotulo} — ${item.descricao}${emAberto}` : item.descricao}
      onClick={() => onIr(item.id)}
      className={`relative flex w-full items-center rounded-xl border text-left text-[13.5px] leading-tight ${
        estreita ? "h-10 justify-center" : "gap-3 px-3.5 py-2.5"
      } ${
        ativo
          ? "border-white/20 bg-[rgb(143_160_196_/_0.24)] font-bold text-white"
          : "border-transparent font-semibold text-[#b9c6dc] hover:bg-white/5 hover:text-white"
      }`}
    >
      {ativo && <span aria-hidden className="absolute top-2.5 bottom-2.5 -left-px w-1 rounded-r bg-laranja" />}
      <Icone nome={item.icone} className={ativo ? "text-laranja" : ""} />
      {!estreita && (
        <span className="min-w-0 flex-1">
          {item.rotulo}
          {item.meta && <span className="mt-0.5 block text-[10.5px] font-medium text-azul-fraca">{item.meta}</span>}
        </span>
      )}
      {contagem > 0 && (estreita ? <ContadorMini n={contagem} /> : <Contador n={contagem} />)}
    </button>
  );
}

function Contador({ n }: { n: number }): ReactNode {
  return (
    <span
      aria-label={`${n} em aberto`}
      className="num min-w-[20px] shrink-0 rounded-full bg-laranja px-1.5 py-px text-center text-[10.5px] font-bold text-azul"
    >
      {n > 999 ? "999+" : n}
    </span>
  );
}

/** O contador da faixa estreita, sobre o canto do ícone. O número exato vai na
 *  dica e no nome acessível do item; aqui basta saber que há o que tratar. */
function ContadorMini({ n }: { n: number }): ReactNode {
  return (
    <span
      aria-hidden
      className="num absolute top-0.5 right-0.5 min-w-[16px] rounded-full bg-laranja px-1 text-center text-[9px] leading-[15px] font-bold text-azul ring-2 ring-azul-escuro"
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

const ROTULO_PAPEL: Record<string, string> = {
  financeiro: "Financeiro",
  gestor_cc: "Gestor de centro de custo",
  admin: "Administrador",
  dev: "Dev · staging",
};

/**
 * Encerra a sessão.
 *
 * `replace` e não `href`: sem isso o botão "voltar" devolve a pessoa a uma tela
 * que já não responde — mesmo motivo do 401 em `lib/api.ts`. E o redirecionamento
 * acontece mesmo se o POST falhar: o cookie pode já ter expirado, e nesse caso
 * insistir na tela antiga é o pior desfecho.
 */
async function sair() {
  try {
    await api.post("/auth/logout", {});
  } catch {
    /* ignorado de propósito — ver acima */
  }
  location.replace("/auth/login");
}

// ── faixa de abas ───────────────────────────────────────────────────────────

/** Também a do Painel do Gestor dentro do Arena (Embutido.tsx). */
export function FaixaAbas<P extends string>({
  abas,
  pagina,
  onIr,
}: {
  abas: { id: P; rotulo: string; meta: string }[];
  pagina: P;
  onIr: (p: P) => void;
}) {
  return (
    <nav
      aria-label="Abas"
      className="thin-scroll flex gap-0.5 overflow-x-auto rounded-t-[10px] bg-azul-escuro px-1.5"
    >
      {abas.map((a) => {
        const ativo = a.id === pagina;
        return (
          <button
            key={a.id}
            type="button"
            aria-current={ativo ? "page" : undefined}
            onClick={() => onIr(a.id)}
            className={`shrink-0 border-b-[3px] px-4 pt-2.5 pb-2 text-left ${
              ativo ? "border-laranja text-white" : "border-transparent text-azul-clara hover:text-white"
            }`}
          >
            <span className="block text-[12.5px] font-semibold">{a.rotulo}</span>
            <span className={`block text-[9.5px] font-medium ${ativo ? "text-[#ffcf94]" : "text-azul-fraca"}`}>
              {a.meta}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
